import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, Minus, Info, Target, Activity, Heart, 
  Moon, Droplets, Brain, ChevronRight, User, Sparkles
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { BottomNav } from '../components/BottomNav';
import { ThemeToggle } from '../components/ThemeToggle';
import { DTSRing } from '../components/DTSRing';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrendsPage() {
  const navigate = useNavigate();
  const [dtsHistory, setDtsHistory] = useState([]);
  const [currentDts, setCurrentDts] = useState(null);
  const [habits, setHabits] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [timeRange, setTimeRange] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [timeRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [historyRes, currentRes, habitsRes, symptomsRes] = await Promise.all([
        axios.get(`${API}/dts/history?days=${timeRange}`).catch(() => ({ data: [] })),
        axios.get(`${API}/dts/latest`).catch(() => ({ data: null })),
        axios.get(`${API}/habits/active`).catch(() => ({ data: [] })),
        axios.get(`${API}/symptoms/recent?limit=20`).catch(() => ({ data: [] }))
      ]);
      setDtsHistory(historyRes.data || []);
      setCurrentDts(currentRes.data);
      setHabits(habitsRes.data || []);
      setSymptoms(symptomsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch trends:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#8BB89F';
    if (score >= 60) return '#F8B64C';
    return '#D54B48';
  };

  // Calculate habit adherence
  const calculateHabitAdherence = () => {
    if (habits.length === 0) return { percentage: 0, label: 'No habits tracked' };
    
    const today = new Date();
    let totalPossible = 0;
    let totalCompleted = 0;
    
    habits.forEach(habit => {
      const daysActive = Math.min(
        timeRange,
        Math.ceil((today - new Date(habit.created_at)) / (1000 * 60 * 60 * 24)) + 1
      );
      totalPossible += daysActive;
      totalCompleted += (habit.completed_days || []).filter(d => {
        const date = new Date(d);
        const daysAgo = Math.ceil((today - date) / (1000 * 60 * 60 * 24));
        return daysAgo <= timeRange;
      }).length;
    });
    
    const percentage = totalPossible > 0 ? Math.round((totalCompleted / totalPossible) * 100) : 0;
    let label = 'Building momentum';
    if (percentage >= 90) label = 'Excellent consistency';
    else if (percentage >= 70) label = 'Good progress';
    else if (percentage >= 50) label = 'Keep going';
    
    return { percentage, label, totalCompleted, totalPossible };
  };

  const adherence = calculateHabitAdherence();

  // Pattern connections (observational, non-diagnostic)
  const getPatternConnections = () => {
    const patterns = [];
    
    // Check for symptom-mood patterns
    if (symptoms.some(s => s.symptoms?.includes('fatigue')) && currentDts?.score < 70) {
      patterns.push({
        observation: 'Fatigue reported when Digital Twin Score is lower',
        suggestion: 'Rest and recovery may help improve your score',
        confidence: 'possible'
      });
    }
    
    // Check for habit-score correlation
    if (adherence.percentage >= 80 && currentDts?.score >= 75) {
      patterns.push({
        observation: 'Higher habit consistency correlates with better DTS',
        suggestion: 'Keep up your daily routines',
        confidence: 'likely'
      });
    }
    
    // Sleep pattern observation
    if (symptoms.some(s => s.symptoms?.includes('sleep_issues'))) {
      patterns.push({
        observation: 'Sleep issues may be affecting your wellness',
        suggestion: 'Consider reviewing your evening routine',
        confidence: 'possible'
      });
    }
    
    // Stress observation
    if (symptoms.some(s => s.symptoms?.includes('anxiety') || s.symptoms?.includes('irritability'))) {
      patterns.push({
        observation: 'Stress-related symptoms have been logged recently',
        suggestion: 'Stress reduction practices may help',
        confidence: 'possible'
      });
    }
    
    return patterns.slice(0, 3); // Max 3 patterns
  };

  const patterns = getPatternConnections();

  // Key metrics
  const avgScore = dtsHistory.length > 0 
    ? Math.round(dtsHistory.reduce((acc, d) => acc + d.score, 0) / dtsHistory.length)
    : currentDts?.score || 0;

  const scoreChange = dtsHistory.length >= 2
    ? dtsHistory[dtsHistory.length - 1]?.score - dtsHistory[0]?.score
    : 0;

  const TrajectoryIcon = currentDts?.trajectory === 'up' ? TrendingUp : 
                         currentDts?.trajectory === 'down' ? TrendingDown : Minus;

  return (
    <div className="min-h-screen bg-background pb-nav">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <h1 className="font-heading font-bold text-lg">Trends</h1>
            <p className="text-xs text-muted-foreground">Your wellness journey</p>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="px-4 py-6 space-y-6">
        {/* Time Range Selector */}
        <div className="flex gap-2">
          {[7, 14, 30].map((days) => (
            <Button
              key={days}
              variant={timeRange === days ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTimeRange(days)}
              className={`rounded-full ${timeRange === days ? 'bg-primary' : ''}`}
              data-testid={`range-${days}-btn`}
            >
              {days}D
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-pulse text-muted-foreground">Loading trends...</div>
          </div>
        ) : (
          <>
            {/* Trend Summary - Plain Language */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="glass-card p-5 border-l-4 border-l-primary">
                <div className="flex items-start gap-3">
                  <Sparkles size={20} className="text-primary shrink-0 mt-0.5" />
                  <div>
                    <h2 className="font-heading font-semibold text-sm mb-1">Your {timeRange}-Day Summary</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {currentDts?.trajectory === 'up' 
                        ? `Your wellness is trending upward. Your Digital Twin Score averaged ${avgScore} with ${adherence.percentage}% habit consistency.`
                        : currentDts?.trajectory === 'down'
                        ? `Your wellness could use some attention. Focus on rest and consistent habits to get back on track.`
                        : `Your wellness is holding steady at an average score of ${avgScore}. ${adherence.percentage >= 70 ? 'Great habit consistency!' : 'Building more consistent habits may help.'}`
                      }
                    </p>
                  </div>
                </div>
              </Card>
            </motion.section>

            {/* Key Metrics */}
            <section>
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
                Key Metrics
              </h2>
              <div className="grid grid-cols-2 gap-3">
                {/* DTS Card */}
                <Card className="glass-card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">Digital Twin Score</span>
                    <TrajectoryIcon 
                      size={14} 
                      className={
                        currentDts?.trajectory === 'up' ? 'text-[#8BB89F]' : 
                        currentDts?.trajectory === 'down' ? 'text-[#D54B48]' : 'text-muted-foreground'
                      } 
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <span className="text-2xl font-bold" style={{ color: getScoreColor(currentDts?.score || 0) }}>
                      {currentDts?.score || '--'}
                    </span>
                    {scoreChange !== 0 && (
                      <span className={`text-xs ${scoreChange > 0 ? 'text-[#8BB89F]' : 'text-[#D54B48]'}`}>
                        {scoreChange > 0 ? '+' : ''}{scoreChange} pts
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {timeRange}D avg: {avgScore}
                  </p>
                </Card>

                {/* Habit Adherence Card */}
                <Card className="glass-card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">Habit Consistency</span>
                    <Target size={14} className="text-primary" />
                  </div>
                  <div className="flex items-end gap-2">
                    <span className="text-2xl font-bold text-primary">{adherence.percentage}%</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{adherence.label}</p>
                </Card>

                {/* Active Habits */}
                <Card className="glass-card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">Active Habits</span>
                    <Activity size={14} className="text-[#F8B64C]" />
                  </div>
                  <span className="text-2xl font-bold">{habits.length}</span>
                  <p className="text-xs text-muted-foreground mt-1">
                    {habits.filter(h => h.current_streak >= 7).length} with 7+ day streaks
                  </p>
                </Card>

                {/* Symptoms Logged */}
                <Card className="glass-card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">Symptoms Logged</span>
                    <Heart size={14} className="text-[#D54B48]" />
                  </div>
                  <span className="text-2xl font-bold">{symptoms.length}</span>
                  <p className="text-xs text-muted-foreground mt-1">
                    Last {timeRange} days
                  </p>
                </Card>
              </div>
            </section>

            {/* Pattern Connections */}
            {patterns.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                    Pattern Observations
                  </h2>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="p-0.5">
                          <Info size={14} className="text-muted-foreground" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[200px]">
                        <p className="text-xs">These are observations, not diagnoses. Patterns may help you understand your wellness.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="space-y-2">
                  {patterns.map((pattern, i) => (
                    <Card key={i} className="glass-card p-4">
                      <div className="flex items-start gap-3">
                        <Brain size={16} className="text-primary shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium">{pattern.observation}</p>
                          <p className="text-xs text-muted-foreground mt-1">{pattern.suggestion}</p>
                          <span className={`text-[10px] mt-2 inline-block px-2 py-0.5 rounded-full ${
                            pattern.confidence === 'likely' 
                              ? 'bg-[#8BB89F]/10 text-[#8BB89F]' 
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {pattern.confidence === 'likely' ? 'Likely pattern' : 'Possible pattern'}
                          </span>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {/* Body Icon / Visual */}
            <section>
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
                Body Overview
              </h2>
              <Card className="glass-card p-6">
                <div className="flex items-center justify-center">
                  <div className="relative">
                    <div className="w-24 h-32 rounded-full bg-gradient-to-b from-primary/20 to-primary/5 flex items-center justify-center">
                      <User size={48} className="text-primary" />
                    </div>
                    {/* Score overlay */}
                    <div 
                      className="absolute -top-2 -right-2 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white"
                      style={{ backgroundColor: getScoreColor(currentDts?.score || 0) }}
                    >
                      {currentDts?.score || '--'}
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    Your Digital Twin represents your overall metabolic wellness state.
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/dashboard')}
                    className="mt-2 text-primary"
                  >
                    View Full Dashboard <ChevronRight size={14} className="ml-1" />
                  </Button>
                </div>
              </Card>
            </section>

            {/* What To Do Next */}
            <section>
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
                What To Do Next
              </h2>
              <div className="space-y-2">
                {adherence.percentage < 70 && (
                  <Card 
                    className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors"
                    onClick={() => navigate('/habits')}
                  >
                    <div className="flex items-center gap-3">
                      <Target size={18} className="text-primary" />
                      <div className="flex-1">
                        <p className="font-medium text-sm">Improve Habit Consistency</p>
                        <p className="text-xs text-muted-foreground">
                          Focus on completing your daily habits to build momentum
                        </p>
                      </div>
                      <ChevronRight size={16} className="text-muted-foreground" />
                    </div>
                  </Card>
                )}
                {currentDts?.trajectory === 'down' && (
                  <Card 
                    className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors"
                    onClick={() => navigate('/daily-plan')}
                  >
                    <div className="flex items-center gap-3">
                      <Moon size={18} className="text-[#8BB89F]" />
                      <div className="flex-1">
                        <p className="font-medium text-sm">Prioritize Recovery</p>
                        <p className="text-xs text-muted-foreground">
                          Your score is trending down—rest and gentle movement may help
                        </p>
                      </div>
                      <ChevronRight size={16} className="text-muted-foreground" />
                    </div>
                  </Card>
                )}
                <Card 
                  className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors"
                  onClick={() => navigate('/symptoms')}
                >
                  <div className="flex items-center gap-3">
                    <Activity size={18} className="text-[#D54B48]" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">Log Today&apos;s Symptoms</p>
                      <p className="text-xs text-muted-foreground">
                        Track how you&apos;re feeling to spot patterns over time
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-muted-foreground" />
                  </div>
                </Card>
              </div>
            </section>
          </>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
